import { MONEY_SCALE, parseHumanAmount, ZERO_MONEY } from '@aeris/money';
import { quoteFor, receiptFor, } from '@aeris/printing';
import { paidLinesOf } from "./document-payments.js";
import { printedCustomerOf } from "./printed-customer.js";
/**
 * O papel de um documento de venda que já existe (F150).
 *
 * Até aqui só o PDV imprimia, no instante da venda. Quem voltava ao balcão
 * três dias depois pedindo a nota não tinha como — a lista de Vendas e a de
 * Orçamentos não ofereciam a ação, e a tela de Orçamentos chegava a dizer
 * "reimprima o papel" sem um botão para isso.
 *
 * Função pura, longe do React, porque é ela que decide QUAL papel sai: o
 * orçamento promete o que vai acontecer, o recibo conta o que aconteceu, e
 * entregar um pelo outro é um mal-entendido no balcão.
 *
 * ## O papel reimpresso é idêntico ao original
 *
 * Mesmo número, mesmo código de barras, sem carimbo de "2ª via". A venda é a
 * mesma; um carimbo faria o cliente achar que a primeira foi cancelada. É o que
 * a tela de Trocas já faz ao reimprimir o comprovante.
 */
const money = (wire) => parseHumanAmount(wire.replace('.', ','), MONEY_SCALE) ?? ZERO_MONEY;
/** `2,5` e não `2.500000`, que é o que uma pessoa lê no papel. */
const quantity = (wire) => wire.replace(/\.?0+$/, '').replace('.', ',');
const lines = (document) => document.lines.map((line) => ({
    description: line.description,
    quantity: quantity(line.quantity),
    unit: line.unit,
    unitPrice: money(line.unitPrice),
    lineTotal: money(line.lineTotal),
    code: line.sku,
    /*
     * A gaveta também (05/10). O papel do PDV a levava e o reimpresso daqui
     * não: o Eduardo imprimiu o 15C pela tela e a peça saiu sem o "F9" que o
     * cadastro tinha. Idêntico ao original é com ela.
     */
    location: line.location,
    discount: money(line.discountAmount),
}));
export const salesDocumentToPrint = (input) => {
    const { document, payments, company, template, voucher } = input;
    if (document.kind === 'quote') {
        return quoteFor({
            template,
            company,
            branchName: document.branchName,
            // Zero não chega aqui: o número é alocado quando o orçamento sai de
            // rascunho, e rascunho não se imprime.
            number: document.number ?? 0,
            issuedAt: new Date(document.issuedAt),
            validUntil: document.validUntil === null ? null : new Date(document.validUntil),
            customerName: document.customerName,
            customer: printedCustomerOf(document),
            salespersonName: document.salespersonName,
            lines: lines(document),
            subtotal: money(document.subtotalAmount),
            discount: money(document.discountAmount),
            total: money(document.totalAmount),
            reservesStock: document.reservesStock,
            documentId: document.id,
        });
    }
    return receiptFor({
        template,
        company,
        voucher,
        branchName: document.branchName,
        number: document.number,
        issuedAt: new Date(document.issuedAt),
        customerName: document.customerName,
        customer: printedCustomerOf(document),
        /*
         * O pedido sai como Pedido (F160): reimpresso, ele saía "Venda nº", e a
         * loja passou a ter dois papéis iguais para documentos diferentes.
         */
        documentKind: document.kind === 'order' ? 'order' : 'sale',
        /*
         * Quem operou o caixa e em qual terminal não está no documento — só a
         * resposta do fechamento os tem. O vendedor NÃO serve de substituto: são
         * papéis diferentes, e pôr um no lugar do outro é atribuir a venda a quem
         * não a fechou. Nulo, que o construtor já sabe não imprimir.
         */
        operatorName: null,
        terminal: null,
        lines: document.lines.map((line) => ({
            description: line.description,
            quantity: quantity(line.quantity),
            unit: line.unit,
            unitPrice: money(line.unitPrice),
            lineTotal: money(line.lineTotal),
            code: line.sku,
            location: line.location,
            discount: money(line.discountAmount),
            priceOverridden: line.priceBasis === 'manual',
        })),
        subtotal: money(document.subtotalAmount),
        discount: money(document.discountAmount),
        total: money(document.totalAmount),
        /*
         * As duas origens do dinheiro, na mesma lista (F159).
         *
         * O recibo de um pedido entregue saía sem forma de pagamento nenhuma: ela
         * mora na baixa do título, e este arquivo só lia os pagamentos do PDV.
         *
         * `out` é estorno e fica de fora: uma soma negativa numa linha de pagamento
         * parece uma cobrança a mais para quem lê o papel no balcão.
         */
        payments: paidLinesOf(payments)
            .filter((line) => !line.isRefund)
            .map((line) => ({ method: line.label, amount: money(line.amount) })),
        changeDue: payments === null ? ZERO_MONEY : money(payments.changeDue),
        documentId: document.id,
    });
};
//# sourceMappingURL=sales-document-print.js.map